<template>
  <div class="address-search-container">
    <v-text-field
      ref="searchInput"
      :value="searchQuery"
      :label="label || $vuetify.lang.t('$vuetify.search_address')"
      :placeholder="placeholder || $vuetify.lang.t('$vuetify.enter_address')"
      :loading="isSearching"
      :error-messages="errorMessage"
      @input="onInputChange"
      @keyup.enter="searchAddress"
      height="46px"
      class="address-input"
      outlined
      dense
    >
      <template v-slot:append v-if="showInfoTooltip">
        <v-tooltip
          bottom
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              small
              v-on="on"
              v-bind="attrs"
            >
              <v-icon color="blue">mdi-information</v-icon>
            </v-btn>
          </template>
          <span>{{ $vuetify.lang.t('$vuetify.space_address_fallback_info') }}</span>
        </v-tooltip>
      </template>
    </v-text-field>
    
    <!-- Results dropdown -->
    <v-list v-if="results.length > 0" class="address-results">
      <v-list-item
        v-for="item in results"
        :key="item.properties.id || item.properties.label"
        @click="onAddressSelected(item)"
        class="address-result-item"
      >
        <v-list-item-content>
          <v-list-item-title>{{ item.properties.label }}</v-list-item-title>
          <v-list-item-subtitle>
            {{ item.properties.city }}, {{ item.properties.postcode }}
          </v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>
    </v-list>
  </div>
</template>

<script>
export default {
  name: 'AddressSearchInput',
  props: {
    label: { type: String, default: '' },
    placeholder: { type: String, default: '' },
    debounceMs: { type: Number, default: 2000 },
    minChars: { type: Number, default: 3 },
    value: { type: String, default: '' },
    spaceAddressPosition: { type: [Object, null], default: null },
    spaceAddress: { type: String, default: '' },
    spaceCity: { type: String, default: '' },
    spaceZipCode: { type: String, default: '' },
    showInfoTooltip: { type: Boolean, default: false }
  },
  
  data() {
    return {
      searchQuery: '',
      results: [],
      debounceTimer: null,
      isLoading: false,
      errorMessage: ''
    }
  },

  computed: {
    isSearching() {
      return this.isLoading || (this.debounceTimer && this.searchQuery && this.searchQuery.trim().length >= this.minChars)
    },
    hasCustomAddress() {
      return this.searchQuery && this.searchQuery.trim() !== ''
    },
    hasSpaceAddressPosition() {
      return this.spaceAddressPosition && 
             this.spaceAddressPosition.latitude && 
             this.spaceAddressPosition.longitude
    }
  },

  watch: {
    value: {
      handler(newValue) {
        // Update searchQuery when value prop changes (external updates)
        this.searchQuery = newValue || ''
      },
      immediate: true
    },
    spaceAddress: {
      immediate: true,
      handler(newAddress) {
        if (newAddress) {
          // Build the full address by ignoring undefined/null/empty values
          const addressParts = []
          
          if (newAddress !== 'undefined') addressParts.push(newAddress)
          if (this.spaceZipCode && this.spaceZipCode !== 'undefined') addressParts.push(this.spaceZipCode)
          if (this.spaceCity && this.spaceCity !== 'undefined') addressParts.push(this.spaceCity)
          
          // Only update if we have valid address parts
          if (addressParts.length > 0) {
            this.searchQuery = addressParts.join(' ')
            this.$emit('input', this.searchQuery)
          }
        }
      }
    }
  },

  methods: {
    onInputChange(value) {
      // Update searchQuery with the new value
      this.searchQuery = value || ''
      
      // Emit input event to sync with parent component
      this.$emit('input', this.searchQuery)
      
      // If the value is empty, clear results and emit cleared event
      if (!value || value.trim() === '') {
        this.clearResults()
        this.$emit('input-cleared')
        return
      }
      
      // Show error immediately for insufficient characters
      if (this.searchQuery.trim().length < this.minChars) {
        this.errorMessage = this.$vuetify.lang.t('$vuetify.type_min_chars', [this.minChars])
        this.results = []
        return
      }
      
      // Clear error and debounce search
      this.errorMessage = ''
      this.debounceSearch()
    },

    debounceSearch() {
      clearTimeout(this.debounceTimer)
      
      // Simplified debounce - only set timer if we have valid input
      this.debounceTimer = setTimeout(() => {
        this.searchAddress()
      }, this.debounceMs)
    },

    clearResults() {
      this.results = []
      this.errorMessage = ''
      this.isLoading = false
      clearTimeout(this.debounceTimer)
      this.debounceTimer = null
    },

    async searchAddress() {
      if (!this.searchQuery) return
      
      const query = this.searchQuery.trim()
      // Validate query length for direct method calls
      if (query.length < this.minChars) {
        this.errorMessage = this.$vuetify.lang.t('$vuetify.type_min_chars', [this.minChars])
        return
      }
      
      clearTimeout(this.debounceTimer)
      this.debounceTimer = null
      this.isLoading = true
      this.errorMessage = ''

      try {
        const response = await fetch(
          `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(query)}`
        )
        this.results = []
        if (response.ok) {
          const data = await response.json()
          this.results = data.features || []
          
          if (this.results.length === 0) {
            this.errorMessage = this.$vuetify.lang.t('$vuetify.no_results_found')
          }
        }
      } catch (error) {
        console.error('Error fetching addresses:', error)
        this.results = []
        this.errorMessage = this.$vuetify.lang.t('$vuetify.address_search_error')
      } finally {
        this.isLoading = false
      }
    },
    onAddressSelected(address) {
      if (address && address.geometry) {
        const [longitude, latitude] = address.geometry.coordinates
        this.$emit('address-selected', [longitude, latitude])
        
        // Emit the full address object for detailed information
        this.$emit('address-details-selected', address)
        
        // Update the searchQuery to show the selected address
        if (address.properties && address.properties.label) {
          this.searchQuery = address.properties.label
          // Emit input event to sync with parent component
          this.$emit('input', this.searchQuery)
        }
        
        this.clearResults()
      }
    }
  },

  mounted() {
    // Clear results when clicking outside
    document.addEventListener('click', (event) => {
      if (!this.$el.contains(event.target)) {
        this.clearResults()
      }
    })
  }
}
</script>

<style scoped>
.address-search-container {
  width: 100%;
  position: relative;
}

.address-input {
  width: 100%;
}

.address-results {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  max-height: 300px;
  overflow-y: auto;
}

.address-result-item {
  cursor: pointer;
}

.address-result-item:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

/* Dark theme support */
:deep(.theme--dark) .address-results {
  background: #424242;
  border-color: rgba(255, 255, 255, 0.12);
}

:deep(.theme--dark) .address-result-item:hover {
  background-color: rgba(255, 255, 255, 0.08);
}
</style>
